import type { ConfigLayer } from '@Map/panel/types';
import SymbolLoader from '@Map/symbols/SymbolLoader';

/**
 * Merges per-layer theme overrides into a layer tree for display (layer panel)
 * and for passing as `layerConfig` to the map. Layers with no entry in `edits`
 * keep their base values from `config`.
 *
 * When `symbolLoader` is supplied, `iconImage` is recomputed from the merged
 * icon+color whenever the edit overrides either field. This corrects the stale
 * `iconImage` that ConfigAdaptor's base convertForPanel() returns when the saved
 * theme has a different icon than the layer's default.
 */
export function mergeThemeEditsIntoConfig(
	config: ConfigLayer[],
	edits: Record<string, Partial<ConfigLayer>>,
	symbolLoader?: SymbolLoader | null,
): ConfigLayer[] {
	return config.map(layer => {
		const layerEdits = layer.id != null ? edits[layer.id] : undefined;
		// A saved theme persists a frozen snapshot of each layer's nested
		// `layers`. For dynamic groups (e.g. the rehab group, whose children come
		// from the live published rehab trees) that snapshot goes stale, so the
		// child *list* must always come from the base config. Separate the edit's
		// children from its scalar overrides and never let the snapshot replace
		// the live tree — the children are instead matched onto base children by
		// id below.
		const { layers: editChildren, ...scalarEdits } = layerEdits ?? {};
		let merged = layerEdits ? { ...layer, ...scalarEdits } : layer;
		if (
			layerEdits &&
			symbolLoader &&
			(scalarEdits.icon !== undefined || scalarEdits.color !== undefined)
		) {
			const icon = merged.icon;
			const color = merged.color;
			if (icon && SymbolLoader.iconValid(icon)) {
				merged = {
					...merged,
					iconImage: symbolLoader.getSvg(
						SymbolLoader.createSymbolId(icon, color),
					),
				};
			}
		}
		if (layer.layers?.length) {
			// The base config drives which children exist. Fold this layer's edit
			// children into `edits` keyed by id so the recursion overlays their
			// scalar overrides onto matching live children; stale edit children
			// with no live match are simply never visited (dropped).
			//
			// The snapshot children are spread *first* so that `edits` (the flat,
			// top-level keyed accumulator that holds the user's in-session toggles)
			// wins for any shared id. Otherwise a freshly toggled rehab action
			// would be clobbered by the loaded theme's frozen visibility for the
			// same id, making individual rehab actions un-editable from the panel.
			const childEdits = editChildren?.length
				? {
						...Object.fromEntries(
							editChildren
								.filter(child => child.id != null)
								.map(child => [child.id as string, child]),
						),
						...edits,
					}
				: edits;
			return {
				...merged,
				layers: mergeThemeEditsIntoConfig(
					layer.layers,
					childEdits,
					symbolLoader,
				),
			};
		}
		return merged;
	});
}

/**
 * Ensures the `iconImage` field in each config layer reflects the *current*
 * theme colour/icon rather than the base ConfigAdaptor default.
 *
 * When a theme is loaded from the backend its `configEdits` contains `icon`
 * and `color` fields but not the pre-computed SVG data-URL (`iconImage`).
 * Without this step, `mergeThemeEditsIntoConfig` leaves `iconImage` as the
 * value computed by `ConfigAdaptor` (base style), so symbol-layer swatches in
 * the LayerPanel still show the default colour.
 *
 * The function only recomputes `iconImage` for layers where a theme edit
 * changed `icon` or `color` but did not supply its own `iconImage`.  Layers
 * that already carry `iconImage` in `pendingEdits` (e.g. from a live editor
 * interaction that calls `layers.getIconSvg`) are left unchanged.
 */
export function enrichIconImages(
	config: ConfigLayer[],
	pendingEdits: Record<string, Partial<ConfigLayer>>,
	getIconSvg: (icon: string, color: string) => string,
): ConfigLayer[] {
	return config.map(layer => {
		const edits = layer.id != null ? pendingEdits[layer.id] : undefined;
		if (edits && (edits.icon || edits.color) && !edits.iconImage) {
			const icon = layer.icon;
			const color = layer.color;
			if (icon && color) {
				const iconImage = getIconSvg(icon, color);
				if (iconImage) {
					const enriched = { ...layer, iconImage };
					if (enriched.layers?.length) {
						return {
							...enriched,
							layers: enrichIconImages(
								enriched.layers,
								pendingEdits,
								getIconSvg,
							),
						};
					}
					return enriched;
				}
			}
		}
		if (layer.layers?.length) {
			return {
				...layer,
				layers: enrichIconImages(
					layer.layers,
					pendingEdits,
					getIconSvg,
				),
			};
		}
		return layer;
	});
}

/**
 * For group rows that have no theme override, sets `color` from the first
 * descendant line layer so the layer list preview matches themed leaf lines.
 */
export function applyGroupLineColorPreview(
	config: ConfigLayer[],
	themeEdits: Record<string, Partial<ConfigLayer>>,
): ConfigLayer[] {
	function firstLineColorInTree(layers: ConfigLayer[]): string | undefined {
		for (const l of layers) {
			if (l.type === 'line' && l.color) {
				return l.color;
			}
			if (l.layers?.length) {
				const nested = firstLineColorInTree(l.layers);
				if (nested) {
					return nested;
				}
			}
		}
		return undefined;
	}

	function walk(nodes: ConfigLayer[]): ConfigLayer[] {
		return nodes.map(node => {
			const childLayers = node.layers?.length
				? walk(node.layers)
				: node.layers;
			const withChildren =
				childLayers !== node.layers
					? { ...node, layers: childLayers }
					: node;

			if (
				childLayers?.length &&
				node.id != null &&
				themeEdits[node.id] === undefined
			) {
				const inherited = firstLineColorInTree(childLayers);
				if (inherited) {
					return { ...withChildren, color: inherited };
				}
			}
			return withChildren;
		});
	}

	return walk(config);
}
